// Feedback: everything the personas wanted to tell the product team about
// aa-app, with the System-1 "worth reporting" verdict and PostHog delivery.
import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../../convex/_generated/api';
import type { Doc } from '../../convex/_generated/dataModel';
import type { FeedbackKind } from '../../convex/shared/validators';
import { fmtDayTime, fmtRelative, humanize, shortName } from '../lib/format';
import { useCast, useNow, type Cast } from '../lib/hooks';
import { useSession, useSimMutation, useSimQuery } from '../lib/session';
import { Avatar } from '../ui/Avatar';
import type { IconName } from '../ui/Icon';
import { Button, Chip, Empty, Meter, Panel, Segmented, Spinner, Stat } from '../ui/primitives';

type Report = Doc<'feedbackReports'>;
type StatusFilter = 'all' | Report['status'];

const KIND_ICON: Record<FeedbackKind, IconName> = { bug: 'bug', ux: 'eye', feature_request: 'sparkle', praise: 'flag', question: 'chat' };
const STATUS_TONE: Record<Report['status'], 'leaf' | 'sky' | 'neutral' | 'danger' | 'sun'> = {
  sent: 'leaf',
  queued: 'sky',
  draft: 'neutral',
  gated_out: 'sun',
  failed: 'danger',
  dismissed: 'neutral',
};
const STATUS_LABEL: Record<Report['status'], string> = {
  sent: 'In PostHog',
  queued: 'Queued',
  draft: 'Draft',
  gated_out: 'Filtered out',
  failed: 'Delivery failed',
  dismissed: 'Cleared',
};

export function FeedbackView() {
  const cast = useCast();
  const now = useNow(30_000);
  const reports = useSimQuery(api.feedback.report.listFeedback, { limit: 100 });
  const [status, setStatus] = useState<StatusFilter>('all');

  const counts = useMemo(() => {
    const c: Record<Report['status'], number> = { sent: 0, queued: 0, draft: 0, gated_out: 0, failed: 0, dismissed: 0 };
    for (const r of reports ?? []) c[r.status]++;
    return c;
  }, [reports]);
  // "All" hides cleared reports; the Cleared filter shows them.
  const shown = reports?.filter((r) => (status === 'all' ? r.status !== 'dismissed' : r.status === status));

  return (
    <div className="page">
      <header className="page-head">
        <h1>Feedback</h1>
        <p>
          When something in aa-app confuses, breaks or delights a persona, a quick System-1 check decides whether it is worth reporting. Reports that pass go to the PostHog
          inbox as support tickets linked to the persona's session replay.
        </p>
      </header>
      <div className="stat-row">
        <Stat icon="send" tone="leaf" label="In PostHog" value={counts.sent} />
        <Stat icon="clock" tone="sky" label="Queued" value={counts.queued + counts.draft} />
        <Stat icon="brain" tone="sun" label="Filtered out" value={counts.gated_out} hint="System-1 said not worth it" />
        <Stat icon="alert" tone="coral" label="Failed" value={counts.failed} />
      </div>
      <Panel
        title="Reports"
        icon="bug"
        actions={
          <Segmented<StatusFilter>
            ariaLabel="Filter by status"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: 'All' },
              { value: 'sent', label: 'Sent' },
              { value: 'gated_out', label: 'Filtered' },
              { value: 'failed', label: 'Failed' },
              { value: 'dismissed', label: 'Cleared' },
            ]}
          />
        }
      >
        {reports === undefined ? <Spinner /> : null}
        {shown?.length === 0 ? <Empty icon="bug" title="No reports yet">Feedback appears once personas use aa-app and the feedback subsystem is on.</Empty> : null}
        <ul className="reports">
          {shown?.map((r) => <ReportCard key={r._id} report={r} cast={cast} now={now} all={reports ?? []} />)}
        </ul>
      </Panel>
    </div>
  );
}

function ReportCard({ report: r, cast, now, all }: { report: Report; cast: Cast | undefined; now: number; all: Report[] }) {
  const [open, setOpen] = useState(false);
  const card = cast?.byId.get(r.personaId);
  const dup = r.gate?.duplicateOfId ? all.find((x) => x._id === r.gate?.duplicateOfId) : undefined;
  return (
    <li className={`report status-${r.status}`}>
      <div className="report-head">
        {card ? (
          <Link href={`/agent/${card.key}`} className="report-who" title={card.name}>
            <Avatar personaKey={card.key} mood={card.mood} size={36} />
          </Link>
        ) : null}
        <div className="report-title">
          <strong>{r.title}</strong>
          <span className="muted">
            {card ? shortName(card.name) : 'Someone'} · <time title={fmtDayTime(r.createdAt)}>{fmtRelative(r.createdAt, now)}</time>
          </span>
        </div>
        <Chip tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Chip>
      </div>
      <div className="report-chips">
        <Chip icon={KIND_ICON[r.kind]}>{humanize(r.kind)}</Chip>
        <Chip tone={r.severity === 'high' ? 'danger' : r.severity === 'medium' ? 'sun' : 'neutral'}>{humanize(r.severity)} severity</Chip>
        {r.appRoute ? <Chip icon="pin">{r.appRoute}</Chip> : null}
        {r.breakpoint ? <Chip icon={r.breakpoint === 'mobile' ? 'mobile' : 'desktop'}>{r.breakpoint}</Chip> : null}
        {r.posthog?.ticketId ? <Chip tone="grape">Ticket {r.posthog.ticketId.slice(0, 8)}</Chip> : null}
      </div>
      {r.gate ? (
        <div className="report-gate">
          <Meter compact label="System-1: worth reporting" detail={`${Math.round(r.gate.worth * 100)}%`} value={r.gate.worth} tone="grape" />
          {dup ? <p className="muted">Looks like a duplicate of “{dup.title}”.</p> : null}
        </div>
      ) : null}
      <p className={`report-body ${open ? '' : 'is-clamped'}`}>{r.body}</p>
      {r.body.length > 220 ? (
        <button type="button" className="link-btn" onClick={() => setOpen((o) => !o)}>
          {open ? 'Show less' : 'Read all'}
        </button>
      ) : null}
      {r.error && r.status !== 'dismissed' ? <p className="form-error">{r.error}</p> : null}
      <ReportActions report={r} />
    </li>
  );
}

/** Owner (admin) controls: send a report to PostHog now (skips the System-1 gate and caps), or clear it. */
function ReportActions({ report: r }: { report: Report }) {
  const { isAdmin } = useSession();
  const send = useSimMutation(api.feedback.report.sendReportNow);
  const dismiss = useSimMutation(api.feedback.report.dismissReport);
  const [busy, setBusy] = useState<'send' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!isAdmin || r.status === 'sent' || r.status === 'queued') return null;
  const run = async (which: 'send' | 'dismiss') => {
    setBusy(which);
    setError(null);
    try {
      await (which === 'send' ? send({ feedbackId: r._id }) : dismiss({ feedbackId: r._id }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="report-actions">
      <Button size="sm" tone="leaf" icon="send" disabled={busy !== null} onClick={() => void run('send')}>
        {busy === 'send' ? 'Sending…' : 'Send to PostHog'}
      </Button>
      {r.status !== 'dismissed' ? (
        <Button size="sm" icon="close" disabled={busy !== null} onClick={() => void run('dismiss')}>
          {busy === 'dismiss' ? 'Clearing…' : 'Clear'}
        </Button>
      ) : null}
      {error ? <p className="form-error">{error}</p> : null}
    </div>
  );
}
